// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — PuestoNode: Custom React Flow Node
// ═══════════════════════════════════════════════════════════════

import { memo, useState } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { useI18n } from '../../../../i18n';
import { NIVEL_FALLBACK } from '../../../../lib/nivel-style';

// ─── Types ───
export interface PuestoNodeData {
  puestoId: string;
  titulo: string;
  codigo: string | null;
  nivel: string;
  nivelPuesto?: { nombre?: string; color?: string | null; colorFondo?: string | null } | null;
  departamento: string | null;
  departamentoColor: string | null;
  persona: { nombre: string; apellidos: string } | null;
  personaId: string | null;
  rolesGobernanza: Array<{ tipoRol: string; nombrePersonalizado?: string }>;
  editMode?: boolean;
  capaActiva?: 'ninguna' | 'gobernanza' | 'vacantes';
  personaClick?: ((personaId: string) => void) | null;
  metadatos?: { posX?: number; posY?: number; posicionManual?: boolean };
  [key: string]: unknown;
}

// ─── Governance Role Icons ───
const ROLE_ICONS: Record<string, string> = {
  PROPIETARIO_PROCESO: 'P',
  RESPONSABLE: 'R',
  APROBADOR: 'A',
  REVISOR: 'V',
  DPO: 'D',
  OFICIAL_CUMPLIMIENTO: 'C',
  AUDITOR_INTERNO: 'I',
  MIEMBRO_CONSEJO: 'M',
  SECRETARIO_CONSEJO: 'S',
  PROPIETARIO_RIESGO: 'R',
  RESPONSABLE_CALIDAD: 'Q',
};

const GOB_COLORS: Record<string, string> = {
  DPO: '#1D4ED8',
  OFICIAL_CUMPLIMIENTO: '#16A34A',
  PROPIETARIO_RIESGO: '#DC2626',
  AUDITOR_INTERNO: '#7C3AED',
  RESPONSABLE_CALIDAD: '#C2410C',
  PROPIETARIO_PROCESO: '#33BCE1',
  MIEMBRO_CONSEJO: '#B45309',
  SECRETARIO_CONSEJO: '#D97706',
  PERSONALIZADO: '#6B7280',
};

// ─── Handle styles ───
const handleVisible: React.CSSProperties = {
  width: 10,
  height: 10,
  borderRadius: '50%',
  background: '#fff',
  border: '2px solid var(--color-acento, #3B82F6)',
  opacity: 1,
  transition: 'opacity 0.15s',
};

const handleHidden: React.CSSProperties = {
  width: 8,
  height: 8,
  opacity: 0,
};

// ─── Component ───
function PuestoNodeComponent({ data, selected }: NodeProps) {
  const { t } = useI18n();
  const d = data as unknown as PuestoNodeData;
  const isEdit = !!d.editMode;
  const barColor = d.departamentoColor || 'var(--color-primario)';
  const np = d.nivelPuesto;
  const nivelStyle = np?.color
    ? { color: np.color, bg: np.colorFondo ?? NIVEL_FALLBACK.bg }
    : NIVEL_FALLBACK;
  const nivelLabel = np?.nombre ?? d.nivel ?? '—';
  const personaName = d.persona ? `${d.persona.nombre} ${d.persona.apellidos}` : null;

  const [hovered, setHovered] = useState(false);

  // Capas de gobernanza y vacantes
  const capa = d.capaActiva ?? 'ninguna';
  const roles = d.rolesGobernanza ?? [];
  const hasRoles = roles.length > 0;
  const capaVacante = capa === 'vacantes' && !d.persona;
  const capaGobBorder =
    capa === 'gobernanza' && hasRoles
      ? (GOB_COLORS[(roles[0] as any)?.tipoRol ?? roles[0]] ?? '#6B7280')
      : null;
  const capaOpacity =
    capa === 'gobernanza' && !hasRoles ? 0.5 : capa === 'vacantes' && !!d.persona ? 1 : 1;

  return (
    <>
      <Handle type="target" position={Position.Top} style={isEdit ? handleVisible : handleHidden} />

      <div
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onDoubleClick={() => {
          if (d.personaId && d.personaClick) d.personaClick(d.personaId);
        }}
        style={{
          width: 220,
          minHeight: 100,
          background: capaVacante ? '#FEF2F2' : '#fff',
          borderRadius: 8,
          boxShadow: d.highlighted
            ? '0 0 0 4px #33BCE1, 0 0 16px rgba(51,188,225,0.4)'
            : d.esFoco
              ? '0 0 0 3px rgba(51,188,225,0.25)'
              : selected
                ? '0 0 0 3px var(--color-acento), 0 4px 12px rgba(0,0,0,0.12)'
                : '0 2px 8px rgba(0,0,0,0.08)',
          border: capaVacante
            ? '2px dashed #DC2626'
            : d.esFoco
              ? '2px solid var(--color-acento, #33BCE1)'
              : capaGobBorder
                ? `2px solid ${capaGobBorder}`
                : selected
                  ? '2px solid var(--color-acento)'
                  : '1px solid #E2E6ED',
          overflow: 'hidden',
          cursor: isEdit ? 'grab' : 'pointer',
          transition: 'box-shadow 0.15s, border-color 0.15s, opacity 0.2s',
          position: 'relative',
          opacity: capaOpacity,
        }}
      >
        {/* Delete button (edit mode, on hover) */}
        {isEdit && hovered && (
          <button
            className="puesto-delete-btn"
            data-action="delete"
            style={{
              position: 'absolute',
              top: 4,
              right: 4,
              zIndex: 2,
              width: 20,
              height: 20,
              borderRadius: '50%',
              border: 'none',
              background: '#DC2626',
              color: '#fff',
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
              padding: 0,
            }}
            title={t.structure.deletePuesto}
          >
            &times;
          </button>
        )}

        {/* Color bar */}
        <div style={{ height: 4, background: barColor }} />

        {/* Content */}
        <div style={{ padding: '10px 12px' }}>
          {/* Title */}
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: '#1A1D23',
              lineHeight: 1.3,
              marginBottom: 4,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {d.titulo}
          </div>

          {/* Person name or Vacante */}
          <div
            style={{
              color: capaVacante ? '#DC2626' : personaName ? '#374151' : '#9CA3AF',
              fontStyle: personaName ? 'normal' : 'italic',
              fontWeight: capaVacante ? 700 : 400,
              fontSize: capaVacante ? 12 : 11,
              marginBottom: 6,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {capaVacante && hasRoles
              ? `⚠️ ${t.structure.vacantLabel}`
              : (personaName ?? t.structure.vacantLabel)}
          </div>

          {/* Level badge + department */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span
              style={{
                display: 'inline-block',
                padding: '1px 6px',
                borderRadius: 4,
                fontSize: 9,
                fontWeight: 600,
                color: nivelStyle.color,
                background: nivelStyle.bg,
                border: `1px solid ${nivelStyle.color}30`,
              }}
            >
              {nivelLabel}
            </span>

            {d.departamento && (
              <span
                style={{
                  fontSize: 9,
                  color: '#6B7280',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 100,
                }}
              >
                {d.departamento}
              </span>
            )}
          </div>

          {/* Governance roles */}
          {roles.length > 0 && (
            <div style={{ display: 'flex', gap: 3, marginTop: 6 }}>
              {roles.map((rol, i) => {
                const tipo = typeof rol === 'string' ? rol : rol.tipoRol;
                const sz = capa === 'gobernanza' ? 24 : 18;
                const color = GOB_COLORS[tipo] ?? 'var(--color-primario)';
                return (
                  <span
                    key={i}
                    title={typeof rol === 'string' ? rol : (rol.nombrePersonalizado ?? tipo)}
                    style={{
                      width: sz,
                      height: sz,
                      borderRadius: '50%',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: capa === 'gobernanza' ? 11 : 9,
                      fontWeight: 700,
                      background: capa === 'gobernanza' ? color : 'var(--color-primario)',
                      color: '#fff',
                      transition: 'all 0.2s',
                    }}
                  >
                    {ROLE_ICONS[tipo] ?? tipo.charAt(0)}
                  </span>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        style={isEdit ? handleVisible : handleHidden}
      />
    </>
  );
}

export const PuestoNode = memo(PuestoNodeComponent);
